import React, { useState } from 'react';
import { SeedanceAPI } from '../services/api';
import { handleImageUpload, triggerFileSelection } from '../utils/uploadUtils';

const Video = () => {
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [firstFrameUrl, setFirstFrameUrl] = useState('');
  const [lastFrameUrl, setLastFrameUrl] = useState('');
  const [videoType, setVideoType] = useState('single');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFirstFrame, setUploadingFirstFrame] = useState(false);
  const [uploadingLastFrame, setUploadingLastFrame] = useState(false);
  
  // Video generation parameters
  const [resolution, setResolution] = useState('1080p');
  const [duration, setDuration] = useState('5');
  const [cameraFixed, setCameraFixed] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [frameRate, setFrameRate] = useState('24');
  const [quantity, setQuantity] = useState(1);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    if (videoType === 'single' && !imageUrl.trim()) {
      setError('Please provide an image URL');
      return;
    }

    if (videoType === 'frames' && (!firstFrameUrl.trim() || !lastFrameUrl.trim())) {
      setError('Please provide both first and last frame URLs');
      return;
    }

    // Validate quantity
    if (quantity < 1 || quantity > 8) {
      setError('Quantity must be between 1 and 8 videos');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setProgress(`Creating ${quantity} video task${quantity > 1 ? 's' : ''}...`);

    try {
      // Construct the full prompt with selected parameters
      const parameters = [
        `--resolution ${resolution}`,
        `--duration ${duration}`,
        `--camerafixed ${cameraFixed}`,
        `--ratio ${aspectRatio}`,
        `--framespersecond ${frameRate}`
      ];
      
      const fullPrompt = `${prompt.trim()} ${parameters.join(' ')}`;
      console.log('Generated prompt with parameters:', fullPrompt);

      // Create multiple video tasks based on quantity
      const videoTasks = [];
      const taskPromises = [];

      for (let i = 0; i < quantity; i++) {
        setProgress(`Creating video task ${i + 1} of ${quantity}...`);
        
        let taskPromise;
        if (videoType === 'single') {
          taskPromise = SeedanceAPI.createVideoTask(fullPrompt, imageUrl);
        } else {
          taskPromise = SeedanceAPI.createVideoWithFrames(fullPrompt, firstFrameUrl, lastFrameUrl);
        }
        
        taskPromises.push(taskPromise);
      }

      // Wait for all tasks to be created
      const responses = await Promise.all(taskPromises);
      const validResponses = responses.filter(response => response.id);
      
      if (validResponses.length === 0) {
        throw new Error('Failed to create any video tasks');
      }

      if (validResponses.length < quantity) {
        setError(`Warning: Only ${validResponses.length} of ${quantity} tasks were created successfully`);
      }

      // Progressive timeout strategy based on quantity and workflow complexity
      let baseTimeout = 300; // 25 minutes for single video
      
      // Increase timeout for multiple videos
      if (quantity > 1) {
        baseTimeout = 600; // 50 minutes base for multiple videos
      }
      
      // Additional timeout for First and Last Frame workflow (more complex)
      if (videoType === 'frames') {
        baseTimeout = Math.floor(baseTimeout * 1.5); // 50% more time for frame interpolation
      }
      
      const maxAttempts = baseTimeout;

      // Enhanced progress feedback with timeout information
      const timeoutMinutes = Math.floor((maxAttempts * 5000) / 60000);
      const progressMessage = validResponses.length > 1 
        ? `Processing ${validResponses.length} videos... (This may take up to ${timeoutMinutes} minutes per video)`
        : `Processing video...`;
      
      setProgress(progressMessage);

      // Poll all tasks until completion
      const pollPromises = validResponses.map((response, index) => 
        SeedanceAPI.pollTaskUntilComplete(response.id, maxAttempts, 5000)
          .then(result => ({ index: index + 1, result }))
          .catch(err => ({ index: index + 1, error: err.message }))
      );

      const results = await Promise.all(pollPromises);
      console.log('Video generation results:', results);

      // Organize results
      const successfulResults = results.filter(r => r.result && !r.error);
      const failedResults = results.filter(r => r.error);

      if (successfulResults.length > 0) {
        // If multiple videos, show all results; if single video, show just the result
        setResult(quantity === 1 ? successfulResults[0].result : {
          type: 'multiple',
          successful: successfulResults,
          failed: failedResults,
          total: quantity
        });
      } else {
        throw new Error('All video generation tasks failed');
      }

      setProgress('');
    } catch (err) {
      setError(err.message || 'Failed to generate video');
      setProgress('');
    } finally {
      setLoading(false);
    }
  };

  const handleImageFileUpload = () => {
    triggerFileSelection((file) => {
      setUploadingImage(true);
      setError('');
      
      handleImageUpload(
        file,
        (dataURL) => {
          setImageUrl(dataURL);
          setUploadingImage(false);
        },
        (errorMessage) => {
          setError(errorMessage);
          setUploadingImage(false);
        }
      );
    });
  };

  const handleFirstFrameUpload = () => {
    triggerFileSelection((file) => {
      setUploadingFirstFrame(true);
      setError('');
      
      handleImageUpload(
        file,
        (dataURL) => {
          setFirstFrameUrl(dataURL);
          setUploadingFirstFrame(false);
        },
        (errorMessage) => {
          setError(errorMessage);
          setUploadingFirstFrame(false);
        }
      );
    });
  };

  const handleLastFrameUpload = () => {
    triggerFileSelection((file) => {
      setUploadingLastFrame(true);
      setError('');
      
      handleImageUpload(
        file,
        (dataURL) => {
          setLastFrameUrl(dataURL);
          setUploadingLastFrame(false);
        },
        (errorMessage) => {
          setError(errorMessage);
          setUploadingLastFrame(false);
        }
      );
    });
  };

  return (
    <div className="px-40 flex flex-1 justify-center py-5">
      <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
        <div className="flex flex-col gap-6 px-4 py-6">
          <h1 className="text-white text-[32px] font-bold leading-tight tracking-[-0.015em]">
            Generate Videos
          </h1>
          
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-white text-sm font-medium">Video Type</label>
              <select
                value={videoType}
                onChange={(e) => setVideoType(e.target.value)}
                className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
              >
                <option value="single">Single Image Reference</option>
                <option value="frames">First and Last Frame</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-white text-sm font-medium">Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the video you want to create..."
                className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] min-h-36 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
              />
            </div>

            {/* Video Generation Parameters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-white text-sm font-medium">Resolution</label>
                <select
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                >
                  <option value="720p">720p (1280x720)</option>
                  <option value="1080p">1080p (1920x1080)</option>
                  <option value="1440p">1440p (2560x1440)</option>
                  <option value="2160p">2160p (3840x2160)</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-white text-sm font-medium">Duration</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                >
                  <option value="1">1 second</option>
                  <option value="2">2 seconds</option>
                  <option value="3">3 seconds</option>
                  <option value="4">4 seconds</option>
                  <option value="5">5 seconds</option>
                  <option value="6">6 seconds</option>
                  <option value="7">7 seconds</option>
                  <option value="8">8 seconds</option>
                  <option value="9">9 seconds</option>
                  <option value="10">10 seconds</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-white text-sm font-medium">Camera Movement</label>
                <div className="flex items-center h-14 px-4 bg-[#2e2839] rounded-xl">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cameraFixed}
                      onChange={(e) => setCameraFixed(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${cameraFixed ? 'bg-[#7c3aed]' : 'bg-[#4a4458]'}`}>
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${cameraFixed ? 'translate-x-6' : 'translate-x-0'}`}></div>
                    </div>
                    <span className="ml-3 text-white text-sm">
                      {cameraFixed ? 'Fixed Camera' : 'Dynamic Camera'}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Additional Parameters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-white text-sm font-medium">Aspect Ratio</label>
                <select
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value)}
                  className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                >
                  <option value="16:9">16:9 (Widescreen)</option>
                  <option value="9:16">9:16 (Portrait)</option>
                  <option value="1:1">1:1 (Square)</option>
                  <option value="4:3">4:3 (Standard)</option>
                  <option value="21:9">21:9 (Ultrawide)</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-white text-sm font-medium">Frame Rate</label>
                <select
                  value={frameRate}
                  onChange={(e) => setFrameRate(e.target.value)}
                  className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                >
                  <option value="24">24 FPS (Cinematic)</option>
                  <option value="30">30 FPS (Standard)</option>
                  <option value="60">60 FPS (Smooth)</option>
                </select>
              </div>
            </div>

            {/* Quantity Selection */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="text-white text-sm font-medium">Quantity</label>
                <div className="relative group">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="text-[#a69db9] cursor-help"
                  >
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9,9h0a3,3,0,0,1,6,0c0,2-3,3-3,3"/>
                    <path d="M12,17h0"/>
                  </svg>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-black text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    Create 1-8 videos simultaneously (max: 8)
                  </div>
                </div>
              </div>
              <select
                value={quantity}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 8) {
                    setQuantity(value);
                    setError(''); // Clear any existing errors
                  } else {
                    setError('Quantity must be between 1 and 8 videos');
                  }
                }}
                className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
              >
                <option value={1}>1 Video</option>
                <option value={2}>2 Videos</option>
                <option value={3}>3 Videos</option>
                <option value={4}>4 Videos</option>
                <option value={5}>5 Videos</option>
                <option value={6}>6 Videos</option>
                <option value={7}>7 Videos</option>
                <option value={8}>8 Videos (Maximum)</option>
              </select>
              {quantity > 1 && (
                <p className="text-[#a69db9] text-xs">
                  ⚠️ Creating {quantity} videos will take longer and use more resources
                </p>
              )}
            </div>

            {/* Parameter Preview */}
            {prompt.trim() && (
              <div className="flex flex-col gap-2">
                <label className="text-white text-sm font-medium">Generated Prompt Preview</label>
                <div className="bg-[#1c1a21] border border-[#2e2839] rounded-xl p-4">
                  <p className="text-[#a69db9] text-sm break-all">
                    {`${prompt.trim()} --resolution ${resolution} --duration ${duration} --camerafixed ${cameraFixed} --ratio ${aspectRatio} --framespersecond ${frameRate}`}
                  </p>
                </div>
              </div>
            )}

            {videoType === 'single' && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <label className="text-white text-sm font-medium">Reference Image URL</label>
                  <div className="relative group">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      fill="currentColor" 
                      viewBox="0 0 256 256"
                      className="text-[#a69db9] cursor-help"
                    >
                      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"/>
                    </svg>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      Please ensure the image URL is publicly accessible
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="form-input flex flex-1 resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                  />
                  <button
                    onClick={handleImageFileUpload}
                    disabled={uploadingImage}
                    className="flex items-center justify-center px-4 py-2 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:bg-[#4c1d95] disabled:cursor-not-allowed text-white rounded-xl transition-colors duration-200 min-w-[100px] h-14"
                  >
                    {uploadingImage ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          fill="currentColor" 
                          viewBox="0 0 256 256"
                        >
                          <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,162.75V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                        </svg>
                        <span className="text-sm">Upload</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}

            {videoType === 'frames' && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-sm font-medium">First Frame URL</label>
                    <div className="relative group">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        fill="currentColor" 
                        viewBox="0 0 256 256"
                        className="text-[#a69db9] cursor-help"
                      >
                        <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"/>
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Please ensure the image URL is publicly accessible
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={firstFrameUrl}
                      onChange={(e) => setFirstFrameUrl(e.target.value)}
                      placeholder="https://example.com/first-frame.jpg"
                      className="form-input flex flex-1 resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                    />
                    <button
                      onClick={handleFirstFrameUpload}
                      disabled={uploadingFirstFrame}
                      className="flex items-center justify-center px-4 py-2 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:bg-[#4c1d95] disabled:cursor-not-allowed text-white rounded-xl transition-colors duration-200 min-w-[100px] h-14"
                    >
                      {uploadingFirstFrame ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            fill="currentColor" 
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,162.75V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                          </svg>
                          <span className="text-sm">Upload</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-sm font-medium">Last Frame URL</label>
                    <div className="relative group">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        fill="currentColor" 
                        viewBox="0 0 256 256"
                        className="text-[#a69db9] cursor-help"
                      >
                        <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"/>
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Please ensure the image URL is publicly accessible
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={lastFrameUrl}
                      onChange={(e) => setLastFrameUrl(e.target.value)}
                      placeholder="https://example.com/last-frame.jpg"
                      className="form-input flex flex-1 resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                    />
                    <button
                      onClick={handleLastFrameUpload}
                      disabled={uploadingLastFrame}
                      className="flex items-center justify-center px-4 py-2 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:bg-[#4c1d95] disabled:cursor-not-allowed text-white rounded-xl transition-colors duration-200 min-w-[100px] h-14"
                    >
                      {uploadingLastFrame ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            fill="currentColor" 
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,162.75V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                          </svg>
                          <span className="text-sm">Upload</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={loading}
              className="flex min-w-[84px] w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-5 bg-[#5b13ec] text-white text-base font-bold leading-normal tracking-[0.015em] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="loading-spinner" />
                  {quantity > 1 ? `Generating ${quantity} Videos...` : 'Generating Video...'}
                </div>
              ) : (
                quantity > 1 ? `Generate ${quantity} Videos` : 'Generate Video'
              )}
            </button>

            {loading && quantity > 1 && (
              <div className="bg-blue-500/20 border border-blue-500 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-blue-400 text-sm font-medium">Creating Multiple Videos</p>
                  <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    {quantity} videos
                  </div>
                </div>
                <p className="text-blue-300 text-xs">
                  ⏳ This may take longer than usual. Each video is being processed simultaneously.
                </p>
                <div className="mt-2 w-full bg-blue-900/30 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{width: '100%'}}></div>
                </div>
              </div>
            )}

            {progress && (
              <div className="bg-blue-500/20 border border-blue-500 rounded-xl p-4">
                <p className="text-blue-400 text-sm">{progress}</p>
              </div>
            )}

            {error && (
              <div className="bg-red-500/20 border border-red-500 rounded-xl p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          {result && (
            <div className="flex flex-col gap-4">
              {result.type === 'multiple' ? (
                // Multiple videos result display
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-white text-xl font-bold">Generated Videos</h2>
                    <div className="bg-[#5b13ec] text-white text-xs px-2 py-1 rounded-full">
                      {result.successful.length} of {result.total} successful
                    </div>
                  </div>
                  
                  {result.failed.length > 0 && (
                    <div className="bg-yellow-500/20 border border-yellow-500 rounded-xl p-4">
                      <p className="text-yellow-400 text-sm">
                        ⚠️ {result.failed.length} video{result.failed.length > 1 ? 's' : ''} failed to generate
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {result.successful.map((videoResult, index) => (
                      <div key={index} className="bg-[#1c1a21] border border-[#2e2839] rounded-xl p-4">
                        <h3 className="text-white text-lg font-semibold mb-2">Video {videoResult.index}</h3>
                        {videoResult.result.content && videoResult.result.content.video_url && (
                          <div className="space-y-4">
                            <div className="flex flex-col gap-2">
                              <video 
                                controls 
                                className="w-full rounded-lg"
                                src={videoResult.result.content.video_url}
                                poster=""
                              >
                                Your browser does not support the video tag.
                              </video>
                              <a 
                                href={videoResult.result.content.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#5b13ec] hover:underline text-sm break-all"
                              >
                                Download Video {videoResult.index}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Single video result display
                <div className="flex flex-col gap-4">
                  <h2 className="text-white text-xl font-bold">Generated Video</h2>
                  <div className="flex flex-col gap-4">
                    {result.content && result.content.video_url && (
                      <div className="bg-[#1c1a21] border border-[#2e2839] rounded-xl p-4">
                        <h3 className="text-white text-lg font-semibold mb-2">Video Result</h3>
                        <div className="space-y-4">
                          <div className="flex flex-col gap-2">
                            <p className="text-[#a69db9] text-sm">Generated Video:</p>
                            <video 
                              controls 
                              className="w-full max-w-2xl rounded-lg"
                              src={result.content.video_url}
                              poster=""
                            >
                              Your browser does not support the video tag.
                            </video>
                            <a 
                              href={result.content.video_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#5b13ec] hover:underline text-sm break-all"
                            >
                              Download Video
                            </a>
                          </div>
                          
                          {result.resolution && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-[#a69db9]">Resolution:</p>
                                <p className="text-white">{result.resolution}</p>
                              </div>
                              {result.duration && (
                                <div>
                                  <p className="text-[#a69db9]">Duration:</p>
                                  <p className="text-white">{result.duration}s</p>
                                </div>
                              )}
                              {result.ratio && (
                                <div>
                                  <p className="text-[#a69db9]">Ratio:</p>
                                  <p className="text-white">{result.ratio}</p>
                                </div>
                              )}
                              {result.framespersecond && (
                                <div>
                                  <p className="text-[#a69db9]">FPS:</p>
                                  <p className="text-white">{result.framespersecond}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {result.status && (
                      <div className="bg-[#1c1a21] border border-[#2e2839] rounded-xl p-4">
                        <p className="text-[#a69db9] text-sm">Status: <span className="text-white">{result.status}</span></p>
                        {result.created_at && (
                          <p className="text-[#a69db9] text-sm">Created: <span className="text-white">{new Date(result.created_at * 1000).toLocaleString()}</span></p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Video;